/**
 * =============================================================================
 * Hook: useReferralAccess
 * Versão: 1.0.0 (2026-05-04)
 *
 * Descrição:
 *   Carrega o estado consolidado de acesso ao módulo Indicação do tenant ativo
 *   via RPC `get_tenant_referral_access`. Retorna:
 *     - tier ativo (free | starter | pro | unlimited)
 *     - limites efetivos (campaigns / programs / affiliates) — null = ilimitado
 *     - uso atual de cada recurso
 *     - saldo de créditos de conversão
 *     - flag `admin_access_blocked` (true quando tier=free e saldo <= 0)
 *
 * Uso:
 *   const { data, isLoading, refetch } = useReferralAccess();
 *   if (data?.admin_access_blocked) { ...show paywall... }
 *   const canCreateCampaign = !data?.limits.campaigns
 *     || data.usage.campaigns < data.limits.campaigns;
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const log = createLogger('useReferralAccess');

export interface ReferralAccess {
  tenant_id: string;
  module_enabled: boolean;
  tier_slug: 'free' | 'referral_starter' | 'referral_pro' | 'referral_unlimited';
  is_paid_tier: boolean;
  limits: {
    campaigns: number | null;
    programs: number | null;
    affiliates: number | null;
  };
  usage: {
    campaigns: number;
    programs: number;
    affiliates: number;
  };
  credits: {
    balance: number;
    free_grant_applied: boolean;
  };
  admin_access_blocked: boolean;
}

/** Verifica se há quota disponível para um recurso (NULL = ilimitado). */
export function hasQuotaAvailable(
  access: ReferralAccess | undefined | null,
  resource: 'campaigns' | 'programs' | 'affiliates',
): boolean {
  if (!access) return false;
  const limit = access.limits[resource];
  if (limit === null || limit === undefined) return true; // ilimitado
  return access.usage[resource] < limit;
}

export function useReferralAccess() {
  return useQuery({
    queryKey: ['referral-access'],
    queryFn: async (): Promise<ReferralAccess | null> => {
      const { data, error } = await supabase.rpc('get_tenant_referral_access', {
        p_tenant_hint: undefined,
      });

      if (error) {
        log.error('Falha ao carregar acesso ao módulo Indicação', { error: error.message });
        throw error;
      }

      return data as unknown as ReferralAccess;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}
